import React, { useState } from "react";
import Modal from "react-modal";
import { useTranslation } from "react-i18next";
import { XMarkIcon, DocumentIcon, PlusIcon } from "@heroicons/react/24/outline";
import AgGridTable from "../../components/controls/AgGridTable";
import Tabs from "../../components/controls/Tabs"; // Assurez-vous d'avoir un composant de Tabs

Modal.setAppElement("#root");

const OrderDetailsModal = ({ isOpen, onClose, order }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("general");

  if (!order) return null;

  const tabs = [
    { id: "general", label: t("modal.generalInfo") },
    { id: "orderItems", label: t("modal.productsServices") },
  ];

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return t("providerRequestsModal.noPhoneNumber");

    const cleaned = ("" + phoneNumber).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phoneNumber;
  };

  const columnDefs = [
    {
      headerName: t("cart.itemName"),
      field: "itemName",
      sortable: true,
      filter: true,
    },
    {
      headerName: t("cart.quantity"),
      field: "quantity",
      sortable: true,
      filter: true,
    },
    {
      headerName: t("cart.unitPrice"),
      field: "unitPrice",
      sortable: true,
      filter: true,
      valueFormatter: (params) => `${params.value.toFixed(2)} $`,
    },
    {
      headerName: t("cart.totalPrice"),
      field: "totalPrice",
      sortable: true,
      filter: true,
      valueFormatter: (params) => `${params.value.toFixed(2)} $`,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={`${t("providers.orderNumber")} ${order.orderNumber}`}
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
        content: {
          width: "90%",
          maxWidth: "1000px",
          margin: "auto",
          borderRadius: "12px",
          padding: "0",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          border: 0,
          zIndex: 200,
        },
      }}
    >
      {/* Header avec Tabs */}
      <div className="bg-accent text-white p-4 flex justify-between items-center">
        <h1 className="text-xl">
          {t("providers.orderNumber")} {order.orderNumber}
        </h1>
        <XMarkIcon className="w-6 h-6 cursor-pointer" onClick={onClose} />
      </div>

      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        fullWidth={true}
      />

      {/* Contenu des tabs */}
      <div className="flex-grow overflow-y-auto p-2 text-primary">
        <div className="flex-grow overflow-y-auto p-2 text-primary">
          {activeTab === "general" && (
            <div className="p-4 grid grid-cols-[auto_1fr] gap-x-6">
              {/* Colonne des labels */}
              <div className="text-gray-600 font-medium space-y-2 text-left">
                <h3>{t("providers.customerName")}:</h3>
                <h3>{t("becomeProvider.phoneNumberLabel")}:</h3>
                <h3>{t("stepOne.eventDate.label")}:</h3>
                <h3>{t("cart.eventAddress")}:</h3>
              </div>

              {/* Colonne des valeurs */}
              <div className="space-y-2">
                <p>{order.customerName}</p>
                <p>{formatPhoneNumber(order.contactPhone)}</p>
                <p>{new Date(order.eventDate).toLocaleDateString("fr-CA")}</p>
                <p>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      order.eventLocationFormatted
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline hover:text-primary/80"
                  >
                    {order.eventLocationFormatted}
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>

        {activeTab === "orderItems" && (
          <div className="p-4">
            <AgGridTable
              columnDefs={columnDefs}
              rowData={order.orderItems}
              pagination={true}
            />
          </div>
        )}
      </div>

      {/* Footer avec boutons */}
      <div className="sticky bottom-0 bg-accent p-4 flex justify-end gap-4 rounded-b-lg border-t">
        <button
          type="button"
          className="flex items-center gap-2 px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium rounded-full"
          onClick={onClose}
        >
          <XMarkIcon className="w-5 h-5" />
          {t("providerRequestsModal.close")}
        </button>
      </div>
    </Modal>
  );
};

export default OrderDetailsModal;
